import React, { createContext, useEffect, useState } from 'react'

import { emptyAddress } from './constants'
import { ShippingFormattedAddress, ShippingSubRoutes } from './types'

export enum AddressType {
  mailing = 'mailing',
  residential = 'residential',
  unverified = 'unverified',
  corrected = 'corrected',
}

type ShippingAddresses = {
  [key in AddressType]?: ShippingFormattedAddress
}

type SetAddressState = (type: AddressType, address: ShippingFormattedAddress) => void
type ShowSubRoute = (subRoute: ShippingSubRoutes) => void

export interface ShippingAddressContextProps {
  addresses: ShippingAddresses
  setAddress: SetAddressState
  didEdit: boolean
  onEdited: () => void
  show: ShowSubRoute
}

export const ShippingAddressContext = createContext<ShippingAddressContextProps>(null)

const useAddressState = (defaultAddress = emptyAddress) => {
  return useState<ShippingFormattedAddress>(defaultAddress)
}

interface ShippingAddressProviderProps {
  children: React.ReactNode
  setSubRoute: (route: ShippingSubRoutes) => void
  defaultAddresses: ShippingAddresses
}

export const ShippingAddressProvider: React.FC<ShippingAddressProviderProps> = ({
  children,
  setSubRoute,
  defaultAddresses,
}) => {
  const [didEdit, setDidEdit] = useState(false)
  const [unverified, setUnverifiedAddress] = useAddressState()
  const [residential, setResidentialAddress] = useAddressState()
  const [mailing, setMailingAddress] = useAddressState()
  const [corrected, setCorrectedAddress] = useAddressState()

  const setAddress: SetAddressState = (type, address) => {
    switch (type) {
      case 'mailing':
        setMailingAddress(address)
        break
      case 'residential':
        setResidentialAddress(address)
        break
      case 'unverified':
        setUnverifiedAddress(address)
        break
      case 'corrected':
        setCorrectedAddress(address)
    }
  }

  const show: ShowSubRoute = subRoute => {
    setSubRoute(subRoute)
  }

  useEffect(() => {
    for (const key in defaultAddresses) {
      setAddress(key as AddressType, defaultAddresses[key])
    }
  }, [])

  return (
    <ShippingAddressContext.Provider
      value={{
        addresses: {
          mailing: mailing,
          residential: residential,
          unverified: unverified,
          corrected: corrected,
        },
        didEdit,
        onEdited() {
          setDidEdit(true)
        },
        setAddress,
        show,
      }}
    >
      {children}
    </ShippingAddressContext.Provider>
  )
}
